import type { ChatMessage, TinyAIPageContext } from '@repo/types';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from '../contexts/analytics/AnalyticsProvider';
import { trpc } from '../lib/trpc/trpc';
import useImpersonate from '../services/auth/useImpersonate';

const ACCOUNTLESS_SESSION_KEY = 'tinyai_accountless_session_id';

interface HistoryPage {
  messages: ChatMessage[];
  nextCursor: string | undefined;
}

interface HistoryQueryData {
  pages: HistoryPage[];
  pageParams: (string | undefined)[];
}

interface UseTinyAIChatParams {
  pageContext: TinyAIPageContext;
  tinyAccountId?: string;
  kitId?: string;
}

export interface UseTinyAIChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  isLoadingMoreHistory: boolean;
  hasMoreHistory: boolean;
  sendMessage: (
    content: string,
    isPredefinedPrompt?: boolean,
    promptId?: number,
    pageContextOverride?: TinyAIPageContext,
  ) => Promise<void>;
  cancelLoading: () => void;
  retryLastMessage: () => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  streamingMessageId: string | null;
  sessionId: string | null;
}

interface ConversationSummary {
  sessionId: string;
  updatedAt: string;
  tinyAccountId?: string | null;
  kitId?: string | null;
}

const GENERIC_ERROR_TEXT = 'Sorry, I encountered an error. Please try again.';

const EMPTY_RESPONSE_ERROR_CODE = 'EMPTY_RESPONSE';

function mapHistoryToMessages(
  historyMessages: Array<{
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
    metadata?: Record<string, unknown>;
    hasFeedback?: boolean;
  }>,
  conversationId: string,
): ChatMessage[] {
  return historyMessages.map(msg => ({
    id: msg.id,
    conversationId,
    senderType: msg.role === 'user' ? 'user' : 'assistant',
    messageText: msg.content,
    metadataJson: msg.metadata || null,
    createdAt: msg.timestamp,
    hasFeedback: msg.hasFeedback ?? false,
  }));
}

// Session key scoped to tinyAccountId only. kit switches stay in the same session
const conversationKey = (tinyAccountId: string | undefined) =>
  ['tinyai-conversation', tinyAccountId ?? null] as const;

const historyKey = (sessionId: string | null) =>
  ['tinyai-history', sessionId] as const;

export const useTinyAIChatTRPC = ({
  pageContext,
  tinyAccountId,
  kitId,
}: UseTinyAIChatParams): UseTinyAIChatReturn => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const { impersonateId } = useImpersonate();
  const currentAssistantMessageRef = useRef<string>('');
  const currentAssistantMessageIdRef = useRef<string | null>(null);

  // Accountless sessionId: resolved synchronously from sessionStorage — no network.
  const accountlessSessionId = useMemo(() => {
    if (tinyAccountId) return null;
    const existing = sessionStorage.getItem(ACCOUNTLESS_SESSION_KEY);
    const sessionId = existing ?? crypto.randomUUID();
    if (!existing) sessionStorage.setItem(ACCOUNTLESS_SESSION_KEY, sessionId);
    return sessionId;
  }, [tinyAccountId]);

  const conversationQuery = useQuery({
    queryKey: conversationKey(tinyAccountId),
    queryFn: async () => {
      return trpc.resolveSession.query({ tinyAccountId: tinyAccountId! });
    },
    enabled: !!tinyAccountId,
    staleTime: 0, // always re-check on mount and account switch
    gcTime: Infinity, // keep result cached between re-renders
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const sessionId: string | null = tinyAccountId
    ? (conversationQuery.data?.sessionId ?? null)
    : accountlessSessionId;
  const isExistingConversation =
    !!tinyAccountId && !(conversationQuery.data?.isNew ?? true);

  // History: only fetched for accounted users with an existing conversation.
  // Accountless + brand-new conversations start with no cached pages; the cache
  // gets seeded lazily when the user sends the first message.
  const historyQuery = useInfiniteQuery({
    queryKey: historyKey(sessionId),
    queryFn: async ({ pageParam }): Promise<HistoryPage> => {
      const page = await trpc.getConversationHistory.query({
        sessionId: sessionId!,
        cursor: pageParam,
        limit: 3,
      });
      return {
        messages: mapHistoryToMessages(page.messages, sessionId!),
        nextCursor: page.nextCursor,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.nextCursor,
    enabled: !!sessionId && isExistingConversation,
    staleTime: Infinity,
  });

  const { trackEvent } = useAnalytics();
  const pageContextRef = useRef(pageContext);
  // Keep the ref in sync during render so sendMessage never reads a stale
  // page/kit/metric after navigation (useEffect would lag one commit).
  pageContextRef.current = pageContext;

  const trackErrorShown = useCallback(
    (errorCode: string | null, errorCategory: string | null) => {
      trackEvent('TinyAI error shown', {
        error_code: errorCode,
        error_category: errorCategory,
        page_context: pageContextRef.current,
      });
    },
    [trackEvent],
  );

  // pages[0] is the newest batch; pages[n] is oldest. Flatten in reverse for
  // chronological display, and dedupe defensively.
  const messages = useMemo<ChatMessage[]>(() => {
    if (!sessionId) return [];
    const pages = historyQuery.data?.pages ?? [];
    const flat = pages
      .slice()
      .reverse()
      .flatMap(p => p.messages);
    const seen = new Set<string>();
    return flat.filter(m => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [historyQuery.data, sessionId]);

  const isLoadingHistory =
    !!tinyAccountId && (conversationQuery.isLoading || historyQuery.isLoading);
  const isLoadingMoreHistory = historyQuery.isFetchingNextPage;
  const hasMoreHistory = !!historyQuery.hasNextPage;

  const setHistoryData = useCallback(
    (
      updater: (
        old: HistoryQueryData | undefined,
      ) => HistoryQueryData | undefined,
    ) => {
      if (!sessionId) return;
      queryClient.setQueryData<HistoryQueryData>(
        historyKey(sessionId),
        updater,
      );
    },
    [queryClient, sessionId],
  );

  const appendToNewestPage = useCallback(
    (newMessages: ChatMessage[]) => {
      setHistoryData(old => {
        if (!old || old.pages.length === 0) {
          return {
            pages: [{ messages: newMessages, nextCursor: undefined }],
            pageParams: [undefined],
          };
        }
        const [firstPage, ...rest] = old.pages;
        return {
          ...old,
          pages: [
            {
              ...firstPage,
              messages: [...firstPage.messages, ...newMessages],
            },
            ...rest,
          ],
        };
      });
    },
    [setHistoryData],
  );

  const updateMessage = useCallback(
    (messageId: string, updater: (msg: ChatMessage) => ChatMessage) => {
      setHistoryData(old => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: page.messages.map(m =>
              m.id === messageId ? updater(m) : m,
            ),
          })),
        };
      });
    },
    [setHistoryData],
  );

  const removeMessage = useCallback(
    (messageId: string) => {
      setHistoryData(old => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: page.messages.filter(m => m.id !== messageId),
          })),
        };
      });
    },
    [setHistoryData],
  );

  // Subscribe to streaming chunks and drive cache updates from there.
  useEffect(() => {
    if (!sessionId) return;

    const subscription = trpc.resultsPortal.subscribeToConversation.subscribe(
      { sessionId },
      {
        onData: (chunk: any) => {
          if (chunk?.type === 'content' && chunk.content) {
            setIsLoading(false);
            const targetId = currentAssistantMessageIdRef.current;
            if (!targetId) return;
            currentAssistantMessageRef.current += chunk.content;
            updateMessage(targetId, m => ({
              ...m,
              messageText: currentAssistantMessageRef.current,
            }));
          } else if (chunk?.type === 'end') {
            const targetId = currentAssistantMessageIdRef.current;
            if (!targetId) {
              setIsLoading(false);
              return;
            }

            const messageId: string | undefined = chunk.metadata?.messageId;
            const sources = Array.isArray(chunk.metadata?.sources)
              ? chunk.metadata.sources
              : undefined;

            if (currentAssistantMessageRef.current) {
              updateMessage(targetId, m => ({
                ...m,
                ...(messageId ? { id: messageId } : {}),
                metadataJson: sources
                  ? { ...(m.metadataJson ?? {}), sources }
                  : m.metadataJson,
              }));
            } else {
              removeMessage(targetId);
              appendToNewestPage([
                {
                  id: crypto.randomUUID(),
                  conversationId: sessionId,
                  senderType: 'assistant' as const,
                  messageText: GENERIC_ERROR_TEXT,
                  metadataJson: {
                    isError: true,
                    errorCode: EMPTY_RESPONSE_ERROR_CODE,
                    errorCategory: 'technical',
                  },
                  createdAt: new Date().toISOString(),
                },
              ]);
              trackErrorShown(EMPTY_RESPONSE_ERROR_CODE, 'technical');
            }

            setIsLoading(false);
            currentAssistantMessageIdRef.current = null;
            setStreamingMessageId(null);
          } else if (chunk?.type === 'error') {
            const errorCode: string | null = chunk.errorCode ?? null;
            const errorCategory: string | null = chunk.errorCategory ?? null;
            console.error(
              '[useTinyAIChatTRPC] Streaming error:',
              errorCode ?? chunk.error,
            );

            // Backend provides the error message for all error types
            const messageText = chunk.error ?? GENERIC_ERROR_TEXT;

            // Remove placeholder message and add error message
            const targetId = currentAssistantMessageIdRef.current;
            if (targetId) {
              removeMessage(targetId);
              appendToNewestPage([
                {
                  id: crypto.randomUUID(),
                  conversationId: sessionId,
                  senderType: 'assistant' as const,
                  messageText: messageText,
                  metadataJson: {
                    isError: true,
                    errorCode,
                    errorCategory,
                  },
                  createdAt: new Date().toISOString(),
                },
              ]);
            }
            setIsLoading(false);
            currentAssistantMessageIdRef.current = null;
            trackErrorShown(errorCode, errorCategory);
            setStreamingMessageId(null);
          }
        },
        onError: (error: unknown) => {
          console.error('[useTinyAIChatTRPC] Subscription error:', error);
          const targetId = currentAssistantMessageIdRef.current;
          if (targetId) {
            removeMessage(targetId);
            appendToNewestPage([
              {
                id: crypto.randomUUID(),
                conversationId: sessionId,
                senderType: 'assistant',
                messageText: GENERIC_ERROR_TEXT,
                metadataJson: {
                  isError: true,
                  errorCode: null,
                  errorCategory: 'technical',
                },
                createdAt: new Date().toISOString(),
              },
            ]);
          }
          setIsLoading(false);
          currentAssistantMessageIdRef.current = null;
          setStreamingMessageId(null);
          trackErrorShown(null, 'technical');
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [
    sessionId,
    updateMessage,
    removeMessage,
    appendToNewestPage,
    trackErrorShown,
  ]);

  const sendMessage = useCallback(
    async (
      content: string,
      isPredefinedPrompt?: boolean,
      promptId?: number,
      pageContextOverride?: TinyAIPageContext,
    ) => {
      if (!content.trim()) return;
      if (impersonateId) return;
      if (isLoadingHistory || !sessionId) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        conversationId: sessionId,
        senderType: 'user',
        messageText: content.trim(),
        metadataJson: null,
        createdAt: new Date().toISOString(),
      };

      setIsLoading(true);
      currentAssistantMessageRef.current = '';

      const assistantMessageId = crypto.randomUUID();
      currentAssistantMessageIdRef.current = assistantMessageId;
      setStreamingMessageId(assistantMessageId);

      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        conversationId: sessionId,
        senderType: 'assistant',
        messageText: '',
        metadataJson: null,
        createdAt: new Date().toISOString(),
      };

      appendToNewestPage([userMessage, assistantMessage]);

      try {
        // Prefer click-time context (metric hooks) over the live ref so a
        // prompt clicked on Gardnerella cannot send Lactobacillus's id.
        const ctx = pageContextOverride ?? pageContextRef.current;
        const contextToSend = {
          url: ctx.url,
          kitId: ctx.kitId,
          tinyAccountId: tinyAccountId,
          isPregnant: ctx.isPregnant,
          isPredefinedPrompt: isPredefinedPrompt ?? false,
          source: ctx.source,
          actionPlanItemId: ctx.actionPlanItemId,
          subMetricId: ctx.subMetricId,
          promptId,
        };

        await trpc.resultsPortal.sendMessage.mutate({
          message: content.trim(),
          sessionId,
          context: contextToSend,
        });
      } catch (error) {
        console.error('[useTinyAIChatTRPC] Failed to send message:', error);
        removeMessage(assistantMessageId);
        appendToNewestPage([
          {
            id: crypto.randomUUID(),
            conversationId: sessionId,
            senderType: 'assistant',
            messageText: GENERIC_ERROR_TEXT,
            metadataJson: {
              isError: true,
              errorCode: null,
              errorCategory: 'technical',
            },
            createdAt: new Date().toISOString(),
          },
        ]);
        setIsLoading(false);
        currentAssistantMessageIdRef.current = null;
        setStreamingMessageId(null);
        trackErrorShown(null, 'technical');
      }
    },
    [
      sessionId,
      isLoadingHistory,
      impersonateId,
      tinyAccountId,
      appendToNewestPage,
      removeMessage,
      trackErrorShown,
    ],
  );

  const retryLastMessage = useCallback(async () => {
    const lastUserMessage = [...messages]
      .reverse()
      .find(msg => msg.senderType === 'user');
    if (!lastUserMessage) return;

    // Remove the last message if it's an error message
    const lastMessage = messages[messages.length - 1];
    const isErrorMsg =
      lastMessage?.senderType === 'assistant' &&
      (lastMessage.metadataJson as { isError?: boolean } | null)?.isError ===
        true;

    if (isErrorMsg) {
      removeMessage(lastMessage.id);
    }

    await sendMessage(lastUserMessage.messageText, false);
  }, [messages, removeMessage, sendMessage]);

  const cancelLoading = useCallback(() => {
    const cancelledId = currentAssistantMessageIdRef.current;
    currentAssistantMessageIdRef.current = null;
    currentAssistantMessageRef.current = '';
    setIsLoading(false);
    setStreamingMessageId(null);
    if (cancelledId) {
      removeMessage(cancelledId);
    }
  }, [removeMessage]);

  const loadMoreHistory = useCallback(async () => {
    if (!historyQuery.hasNextPage || historyQuery.isFetchingNextPage) return;
    try {
      await historyQuery.fetchNextPage();
    } catch (error) {
      console.error('[useTinyAIChatTRPC] Failed to load more history:', error);
    }
  }, [historyQuery]);

  return {
    messages,
    isLoading,
    isLoadingHistory,
    isLoadingMoreHistory,
    hasMoreHistory,
    sendMessage,
    cancelLoading,
    loadMoreHistory,
    retryLastMessage,
    streamingMessageId,
    sessionId,
  };
};
